import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import { ADMIN } from "@/constants/testIds";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin");
  }, [user, navigate]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (res.ok) navigate("/admin");
    else setError(res.error || "Login failed");
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />
      <main className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-md fade-in-up">
          <div className="mb-8 text-center">
            <div className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-[#ccfbf1] text-[#0f766e]">
              <Lock className="w-5 h-5" />
            </div>
            <h1 className="font-display mt-5 text-3xl font-semibold tracking-tight text-slate-900">
              Admin sign-in
            </h1>
            <p className="mt-2 text-slate-500 text-sm">
              Access leads, exports and quiz analytics.
            </p>
          </div>

          <form
            onSubmit={submit}
            data-testid={ADMIN.loginForm}
            className="bg-white border border-slate-200 rounded-xl p-7 space-y-5"
          >
            <div>
              <Label htmlFor="admin-email" className="text-slate-700">Email</Label>
              <Input
                id="admin-email"
                type="email"
                data-testid={ADMIN.loginEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5 h-11 border-slate-200 focus-visible:ring-[#0f766e] focus-visible:border-[#0f766e]"
                required
              />
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-slate-700">Password</Label>
              <Input
                id="admin-password"
                type="password"
                data-testid={ADMIN.loginPwField}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11 border-slate-200 focus-visible:ring-[#0f766e] focus-visible:border-[#0f766e]"
                required
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md p-3">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid={ADMIN.loginSubmit}
              className="w-full h-11 bg-[#0f766e] hover:bg-[#115e59] text-white"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
